import { Component, Suspense, lazy, useEffect, useRef, useState } from "react";
import { Reveal } from "@/components/Reveal";

// Lazy-load real: el chunk 3D (three + fiber) sólo se descarga cuando el
// usuario se acerca a esta sección. React 19-safe (fiber@9, no drei).
const DiamondCanvas = lazy(() => import("./DiamondCanvas"));

/**
 * DiamondViewer3D — "See the brilliance". Renderiza el diamante 3D real
 * si el navegador soporta WebGL y el bundle carga sin errores. Si algo
 * falla (WebGL bloqueado, thrown error de fiber, reduced motion), muestra
 * el fallback estático luxury (conic-gradient) sin crashear.
 */
export default function DiamondViewer3D() {
  const sectionRef = useRef(null);
  const [enabled, setEnabled] = useState(false);
  const [webglOk, setWebglOk] = useState(true);
  const [canvasFailed, setCanvasFailed] = useState(false);
  const [reduced, setReduced] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return;
    setReduced(window.matchMedia?.("(prefers-reduced-motion: reduce)").matches || false);
    // WebGL feature detect
    try {
      const c = document.createElement("canvas");
      const gl = c.getContext("webgl2") || c.getContext("webgl");
      if (!gl) setWebglOk(false);
    } catch {
      setWebglOk(false);
    }
    // IntersectionObserver-gated mount (con rootMargin generoso para
    // que el chunk se descargue justo antes de aparecer)
    const el = sectionRef.current;
    if (!el || !("IntersectionObserver" in window)) {
      setEnabled(true);
      return;
    }
    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setEnabled(true);
          io.disconnect();
        }
      },
      { rootMargin: "300px" }
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  // Cuando el Canvas dispara un error runtime, aislamos el problema
  // mostrando el fallback — el resto de la app no se ve afectada.
  const handleCanvasError = () => {
    setCanvasFailed(true);
    // eslint-disable-next-line no-console
    console.warn("[DiamondViewer3D] WebGL canvas failed — falling back to static.");
  };

  const showCanvas = enabled && webglOk && !reduced && !canvasFailed;

  return (
    <section
      id="diamond-3d"
      ref={sectionRef}
      data-testid="diamond-3d-section"
      className="relative overflow-hidden bg-[#0a0a0b] py-24 lg:py-32"
    >
      <div className="pointer-events-none absolute inset-0">
        <div
          className="absolute inset-0"
          style={{
            background:
              "radial-gradient(ellipse at 50% 40%, rgba(255,245,220,0.08) 0%, rgba(255,245,220,0) 55%)",
          }}
        />
      </div>
      <div className="relative mx-auto grid max-w-6xl grid-cols-1 items-center gap-10 px-5 lg:grid-cols-2 lg:px-10">
        <Reveal y={30}>
          <span className="eyebrow text-[11px] text-white/60">Interactive</span>
          <h2 className="mt-3 font-serif-display text-3xl font-medium text-white sm:text-4xl">
            See the brilliance from every angle.
          </h2>
          <p className="mt-4 max-w-md font-body text-sm leading-relaxed text-white/70">
            Move the stone. Watch how VVS moissanite catches every ray of light —
            with the same fire you’ll experience in person.
          </p>
          <ul className="mt-6 grid grid-cols-2 gap-y-3 text-[13px] text-white/70">
            <li>· 58 facets · brilliant cut</li>
            <li>· VVS clarity · D color</li>
            <li>· 9.25 Mohs hardness</li>
            <li>· Real-time render</li>
          </ul>
        </Reveal>

        <div
          data-testid="diamond-3d-stage"
          className="relative aspect-square w-full overflow-hidden rounded-2xl border border-white/10 bg-gradient-to-br from-[#111] to-[#050505]"
        >
          {showCanvas ? (
            <Suspense fallback={<StagedFallback reduced={false} />}>
              <ErrorBoundary onError={handleCanvasError}>
                <DiamondCanvas onError={handleCanvasError} />
              </ErrorBoundary>
            </Suspense>
          ) : (
            <StagedFallback reduced={reduced} />
          )}
        </div>
      </div>
    </section>
  );
}

/* -------------------------------------------------------------------------- */
/*  ErrorBoundary específico del canvas — aisla cualquier throw runtime      */
/* -------------------------------------------------------------------------- */
class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }
  static getDerivedStateFromError() {
    return { hasError: true };
  }
  componentDidCatch(error) {
    // eslint-disable-next-line no-console
    console.warn("[DiamondCanvas] runtime error caught by ErrorBoundary:", error);
    this.props.onError?.(error);
  }
  render() {
    if (this.state.hasError) return <StagedFallback reduced={false} />;
    return this.props.children;
  }
}

/* -------------------------------------------------------------------------- */
/*  Static fallback (only when WebGL missing / reduced-motion / error)        */
/* -------------------------------------------------------------------------- */
function StagedFallback({ reduced }) {
  return (
    <div className="flex h-full w-full items-center justify-center">
      <div
        aria-hidden="true"
        data-testid="diamond-3d-fallback"
        className="relative h-40 w-40 rotate-45 rounded-lg border border-white/20"
        style={{
          background:
            "conic-gradient(from 45deg, rgba(255,255,255,0.9), rgba(200,220,255,0.35), rgba(255,240,220,0.75), rgba(255,255,255,0.9))",
          boxShadow: "0 0 60px 8px rgba(200,220,255,0.25)",
          animation: reduced ? "none" : "diamond-idle 8s ease-in-out infinite",
        }}
      />
      <style>{`
        @keyframes diamond-idle {
          0%, 100% { transform: rotate(45deg) translateZ(0); filter: brightness(1); }
          50%      { transform: rotate(55deg) translateZ(0); filter: brightness(1.12); }
        }
      `}</style>
    </div>
  );
}
